import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  VideoCamera,
  UploadSimple,
  ArrowClockwise,
  Sparkle,
  ChartBar,
  Trash,
  PencilSimple,
  FilePdf,
  Share,
  Cow,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { api, API, TOKEN_KEY, formatBRL, formatNum, formatDate, downloadAnalysisPdf, shareAnalysisPdf, toastWithQuota, showQuotaBlocked } from "@/lib/gado";

const MAX_MB = 300;

function ConfidenceBadge({ value }) {
  const pct = Math.round((value || 0) * 100);
  const color =
    pct >= 80
      ? "border-gado-green/30 bg-gado-green/10 text-gado-green"
      : pct >= 60
      ? "border-gado-gold/40 bg-gado-gold/10 text-gado-gold"
      : "border-destructive/40 bg-destructive/10 text-destructive";
  return (
    <Badge variant="outline" className={`${color} text-[10px] px-2 py-0.5`} data-testid="confidence-badge">
      Confiança {pct}%
    </Badge>
  );
}

function DistributionChart({ perAnimal }) {
  if (!perAnimal || perAnimal.length === 0) return null;
  const weights = perAnimal.map((a) => Number(a.weight_kg) || 0);
  const minW = Math.min(...weights);
  const maxW = Math.max(...weights);
  const bins = 6;
  const step = (maxW - minW) / bins || 1;
  const counts = Array(bins).fill(0);
  weights.forEach((w) => {
    const b = Math.min(bins - 1, Math.max(0, Math.floor((w - minW) / step)));
    counts[b] += 1;
  });
  const maxCount = Math.max(...counts) || 1;
  return (
    <div className="mt-2" data-testid="weight-distribution">
      <div className="flex items-end gap-1 h-24">
        {counts.map((c, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-sm bg-gado-green/70 hover:bg-gado-green transition-colors"
            style={{ height: `${(c / maxCount) * 100}%` }}
            title={`${(minW + i * step).toFixed(0)}–${(minW + (i + 1) * step).toFixed(0)} kg · ${c} animais`}
          />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-gado-muted mt-1 font-mono">
        <span>{formatNum(minW, 0)} kg</span>
        <span>{formatNum(maxW, 0)} kg</span>
      </div>
    </div>
  );
}

function AnalysisResult({ analysis, onCalibrate, onDelete }) {
  const r = analysis?.result || {};
  const [calibOpen, setCalibOpen] = useState(false);
  const [realWeight, setRealWeight] = useState("");
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let url = null;
    (async () => {
      if (!analysis?.analysis_id) return;
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        const res = await fetch(`${API}/analyses/${analysis.analysis_id}/thumbnail`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        if (!cancelled) setThumbUrl(url);
      } catch {
        /* thumbnail is optional */
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [analysis?.analysis_id]);

  return (
    <div className="rounded-xl border border-gado-line bg-white p-5 space-y-4" data-testid="analysis-result">
      {thumbUrl && (
        <img
          src={thumbUrl}
          alt="Frame do vídeo analisado"
          data-testid="analysis-thumbnail"
          className="w-full max-h-56 object-cover rounded-lg border border-gado-line"
        />
      )}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-gado-gold" />
            <p className="text-[10px] uppercase tracking-widest text-gado-muted">Análise por IA · Gemini 3.1 Pro</p>
          </div>
          <p className="font-cabinet text-lg font-bold text-gado-ink mt-0.5">
            Lote de {r.total_animals || 0} cabeça{r.total_animals === 1 ? "" : "s"}
          </p>
          <p className="text-xs text-gado-muted">
            {r.breed_guess || "—"} · {r.category_guess || "—"} · {formatDate(analysis?.created_at)}
          </p>
        </div>
        <ConfidenceBadge value={r.confidence} />
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-gado-bg/60 py-3 px-1">
          <p className="text-[10px] uppercase tracking-wider text-gado-muted">Cabeças</p>
          <p className="font-mono font-bold text-gado-ink text-lg">{r.total_animals || 0}</p>
        </div>
        <div className="rounded-lg bg-gado-bg/60 py-3 px-1">
          <p className="text-[10px] uppercase tracking-wider text-gado-muted">Peso médio</p>
          <p className="font-mono font-bold text-gado-ink text-lg">{formatNum(r.avg_weight_kg, 0)} kg</p>
        </div>
        <div className="rounded-lg bg-gado-green/5 py-3 px-1">
          <p className="text-[10px] uppercase tracking-wider text-gado-muted">Peso total</p>
          <p className="font-mono font-bold text-gado-green text-lg">{formatNum(r.total_weight_kg, 0)} kg</p>
        </div>
      </div>

      {r.notes && (
        <p className="text-xs italic text-gado-muted border-l-2 border-gado-gold pl-2">{r.notes}</p>
      )}

      {r.per_animal && r.per_animal.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] uppercase tracking-wider text-gado-muted">Distribuição de pesos</p>
            <ChartBar size={12} className="text-gado-muted" />
          </div>
          <DistributionChart perAnimal={r.per_animal} />
        </div>
      )}

      <div className="flex items-center gap-2 pt-2 border-t border-gado-line">
        <Button
          onClick={() => {
            const fn = `Analise_IA_${(analysis.analysis_id || "").slice(-6).toUpperCase()}.pdf`;
            toastWithQuota(downloadAnalysisPdf(analysis.analysis_id, fn), {
              loading: "Gerando PDF…",
              success: "PDF baixado",
              error: "Falha ao gerar PDF",
            });
          }}
          data-testid="pdf-analysis-button"
          className="flex-1 bg-gado-green hover:bg-gado-greenHover text-white"
        >
          <FilePdf size={16} className="mr-1.5" /> Baixar PDF
        </Button>
        <Button
          onClick={() => {
            toast.promise(shareAnalysisPdf(analysis), {
              loading: "Preparando PDF…",
              success: (r) => {
                if (r.shared) return "Compartilhado";
                if (r.cancelled) return "Compartilhamento cancelado";
                return "PDF baixado (compartilhamento indisponível neste dispositivo)";
              },
              error: (e) => (e?.code === 402 ? e.message : "Falha ao gerar PDF"),
            });
          }}
          data-testid="share-analysis-button"
          variant="outline"
          className="border-gado-green/40 text-gado-green hover:bg-gado-green/5"
        >
          <Share size={16} className="mr-1.5" /> Compartilhar
        </Button>
        <Button
          onClick={() => setCalibOpen(true)}
          variant="outline"
          data-testid="calibrate-button"
        >
          <PencilSimple size={16} className="mr-1.5" /> Calibrar
        </Button>
        <Button
          onClick={() => onDelete?.(analysis)}
          variant="outline"
          className="text-destructive hover:text-destructive"
          data-testid="delete-analysis-button"
        >
          <Trash size={16} />
        </Button>
      </div>

      <Dialog open={calibOpen} onOpenChange={setCalibOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gado-ink">Calibrar estimativa</DialogTitle>
            <DialogDescription>
              Informe o peso REAL total desse lote (balança oficial) para melhorar futuras estimativas.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-wider text-gado-muted">Peso real total (kg)</Label>
            <Input
              type="text"
              inputMode="decimal"
              className="mt-1.5"
              value={realWeight}
              onChange={(e) => setRealWeight(e.target.value.replace(/[^0-9.,]/g, ""))}
              placeholder="ex: 5250"
              data-testid="calib-real-weight"
            />
            <p className="text-xs text-gado-muted mt-2">
              Estimativa atual: <b>{formatNum(r.total_weight_kg, 0)} kg</b>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCalibOpen(false)}>Cancelar</Button>
            <Button
              onClick={async () => {
                const v = parseFloat(realWeight.replace(",", "."));
                if (!v || v <= 0) return toast.error("Informe um peso válido");
                setCalibOpen(false);
                await onCalibrate?.(analysis, v);
                setRealWeight("");
              }}
              data-testid="calib-save"
              className="bg-gado-green hover:bg-gado-greenHover text-white"
            >
              Salvar calibração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AnaliseVideo() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [hint, setHint] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [videoDuration, setVideoDuration] = useState(null); // segundos — decide sync vs 2º plano
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/analyses");
        setHistory(data || []);
      } catch {
        /* ignore — empty history */
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 500 * 1024 * 1024) {
      toast.error("Vídeo muito grande — máx 500 MB");
      return;
    }
    setFile(f);
    setCurrent(null);
    setVideoDuration(null);
    // Detecta a duração para decidir entre análise imediata ou segundo plano
    const url = URL.createObjectURL(f);
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      const dur = v.duration || 0;
      URL.revokeObjectURL(url);
      setVideoDuration(dur);
      if (dur > 0 && dur <= 300 && f.size > MAX_MB * 1024 * 1024) {
        toast.error(`Vídeos de até 5 min: máx ${MAX_MB} MB. Acima de 5 min, processamos em 2º plano (máx 500 MB).`);
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
      }
    };
    v.onerror = () => URL.revokeObjectURL(url);
    v.src = url;
  };

  const loadDemoVideo = async () => {
    try {
      const res = await fetch("/demo/gado-demo.mp4");
      if (!res.ok) throw new Error("Falha ao carregar vídeo demo");
      const blob = await res.blob();
      const demoFile = new File([blob], "gado-demo.mp4", { type: "video/mp4" });
      setFile(demoFile);
      setCurrent(null);
      setVideoDuration(null);
      setHint("Vídeo demo · pasto verde com gado");
      toast.success("Vídeo demo carregado — clique em Analisar Lote");
    } catch (e) {
      toast.error(e.message || "Falha ao carregar vídeo demo");
    }
  };

  const isLongVideo = videoDuration != null && videoDuration > 300; // > 5 min → 2º plano

  // Polling: enquanto houver análise "processing" no histórico, consulta a cada 10s
  useEffect(() => {
    if (!history.some((a) => a.status === "processing")) return;
    const t = setInterval(async () => {
      for (const a of history.filter((x) => x.status === "processing")) {
        try {
          const { data } = await api.get(`/analyses/${a.analysis_id}`);
          if (data.status === "processing") continue;
          setHistory((prev) => prev.map((x) => (x.analysis_id === a.analysis_id ? data : x)));
          if (data.status === "completed") {
            toast.success(`Análise concluída: ${data.result?.total_animals || 0} cabeças`, { duration: 8000 });
            if (current?.analysis_id === a.analysis_id) setCurrent(data);
          } else if (data.status === "failed") {
            toast.error("Falha ao analisar o vídeo em segundo plano");
            if (current?.analysis_id === a.analysis_id) setCurrent(data);
          }
        } catch {
          /* tenta novamente no próximo ciclo */
        }
      }
    }, 10000);
    return () => clearInterval(t);
  }, [history, current?.analysis_id]);

  const startBackgroundAnalysis = async () => {
    setUploading(true);
    setProgress(5);
    const form = new FormData();
    form.append("video", file);
    if (hint) form.append("hint", hint);
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const record = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/analyses/video/background`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) setProgress(Math.min(90, Math.round((ev.loaded / ev.total) * 90)));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); } catch { reject(new Error("Resposta inválida")); }
          } else {
            try {
              const j = JSON.parse(xhr.responseText);
              reject(Object.assign(new Error(j.detail || "Falha ao enviar"), { code: xhr.status }));
            } catch { reject(new Error("Falha ao enviar")); }
          }
        };
        xhr.onerror = () => reject(new Error("Erro de rede"));
        xhr.send(form);
      });
      setProgress(100);
      setHistory((prev) => [record, ...prev]);
      setCurrent(record); // mostra o card "em segundo plano"
      setFile(null);
      setHint("");
      setVideoDuration(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success(
        "Vídeo recebido! Processando em segundo plano — avisaremos quando a análise terminar.",
        { duration: 7000 }
      );
    } catch (e) {
      if (e?.code === 402) showQuotaBlocked(e.message);
      else toast.error(e.message || "Falha ao enviar vídeo");
    } finally {
      setTimeout(() => { setUploading(false); setProgress(0); }, 500);
    }
  };

  const startAnalysis = async () => {
    if (!file) return toast.error("Selecione um vídeo primeiro");
    if (isLongVideo) return startBackgroundAnalysis();
    setUploading(true);
    setProgress(5);
    const form = new FormData();
    form.append("video", file);
    if (hint) form.append("hint", hint);
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      // Real XHR to get upload progress (fetch has no upload progress in the browser)
      const record = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/analyses/video`);
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            setProgress(Math.min(60, Math.round((ev.loaded / ev.total) * 60)));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch {
              reject(new Error("Resposta inválida"));
            }
          } else {
            try {
              const j = JSON.parse(xhr.responseText);
              reject(Object.assign(new Error(j.detail || "Falha na análise"), { code: xhr.status }));
            } catch {
              reject(new Error("Falha na análise"));
            }
          }
        };
        xhr.onerror = () => reject(new Error("Erro de rede"));
        xhr.send(form);
      });
      // Simulate analysis progress phases (server processes after upload)
      const stops = [70, 82, 92, 98];
      stops.forEach((p, i) => setTimeout(() => setProgress(p), 400 + i * 500));
      setTimeout(() => setProgress(100), 2400);
      setCurrent(record);
      setHistory((prev) => [record, ...prev]);
      setFile(null);
      setHint("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success(`Lote analisado: ${record.result?.total_animals || 0} cabeças`);
    } catch (e) {
      if (e?.code === 402) showQuotaBlocked(e.message);
      else toast.error(e.message || "Falha na análise");
    } finally {
      setTimeout(() => {
        setUploading(false);
        setProgress(0);
      }, 500);
    }
  };

  const onCalibrate = async (a, real) => {
    try {
      await api.put(`/analyses/${a.analysis_id}/calibrate`, {
        real_total_weight_kg: real,
      });
      toast.success("Calibração salva. Futuras estimativas usarão esse fator.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao calibrar");
    }
  };

  const onDelete = async (a) => {
    try {
      await api.delete(`/analyses/${a.analysis_id}`);
      setHistory((prev) => prev.filter((x) => x.analysis_id !== a.analysis_id));
      if (current?.analysis_id === a.analysis_id) setCurrent(null);
      toast.success("Análise removida");
    } catch {
      toast.error("Falha ao remover");
    }
  };

  return (
    <div className="space-y-6" data-testid="analise-video-page">
      <div>
        <div className="flex items-center gap-2">
          <h1 className="font-cabinet text-2xl md:text-3xl font-bold text-gado-ink tracking-tight">
            Análise por IA
          </h1>
          <Badge variant="outline" className="border-gado-gold/40 bg-gado-gold/10 text-gado-gold text-[10px]">
            Beta
          </Badge>
        </div>
        <p className="text-gado-muted">
          Grave ou envie um vídeo do lote — até 5 min a IA analisa na hora; vídeos mais longos são processados em segundo plano, com aviso quando terminar.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 space-y-4">
          {/* Upload / capture card */}
          <div className="rounded-xl border border-gado-line bg-white p-5 space-y-4">
            {!previewUrl ? (
              <div className="border-2 border-dashed border-gado-line rounded-lg p-8 text-center">
                <Cow size={40} className="mx-auto text-gado-green mb-2" weight="fill" />
                <p className="text-sm text-gado-ink font-medium">Envie ou grave um vídeo do lote</p>
                <p className="text-xs text-gado-muted mt-1">MP4 ou MOV · até {MAX_MB} MB na hora (até 5 min) · até 500 MB em 2º plano</p>
                <div className="flex flex-col sm:flex-row gap-2 mt-4 justify-center">
                  <Button
                    onClick={() => fileRef.current?.click()}
                    data-testid="pick-video-button"
                    className="bg-gado-green hover:bg-gado-greenHover text-white"
                  >
                    <UploadSimple size={18} className="mr-2" /> Enviar vídeo
                  </Button>
                  <label className="inline-flex">
                    <input
                      type="file"
                      accept="video/*"
                      capture="environment"
                      className="hidden"
                      onChange={onPickFile}
                      data-testid="capture-video-input"
                    />
                    <span className="inline-flex items-center justify-center px-4 py-2 rounded-md border border-gado-line text-sm cursor-pointer hover:bg-gado-line/40">
                      <VideoCamera size={18} className="mr-2" /> Usar câmera
                    </span>
                  </label>
                </div>
                <div className="mt-4 pt-4 border-t border-gado-line/60">
                  <button
                    onClick={loadDemoVideo}
                    data-testid="load-demo-video"
                    className="inline-flex items-center gap-2 text-xs font-semibold text-gado-green hover:text-gado-greenHover underline underline-offset-4"
                  >
                    <Sparkle size={14} weight="fill" className="text-gado-gold" />
                    Testar com vídeo demo (sem gravar)
                  </button>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/*"
                  className="hidden"
                  onChange={onPickFile}
                  data-testid="pick-video-input"
                />
              </div>
            ) : (
              <div className="space-y-3">
                <video
                  src={previewUrl}
                  controls
                  playsInline
                  className="w-full rounded-lg max-h-72 bg-black"
                  data-testid="video-preview"
                />
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gado-muted">Observação (opcional)</Label>
                  <Input
                    className="mt-1.5"
                    value={hint}
                    onChange={(e) => setHint(e.target.value)}
                    placeholder="ex: Nelore adultos, curral coberto"
                    data-testid="hint-input"
                  />
                </div>
                {isLongVideo && !uploading && (
                  <div
                    className="rounded-lg border border-gado-gold/40 bg-gado-gold/10 px-3 py-2.5 text-xs text-gado-ink flex items-start gap-2"
                    data-testid="long-video-notice"
                  >
                    <VideoCamera size={16} className="text-gado-gold shrink-0 mt-0.5" weight="fill" />
                    <span>
                      Vídeo com <b>{Math.round(videoDuration / 60)} min</b> — será processado em{" "}
                      <b>segundo plano</b>. Você pode continuar usando o app e receberá um aviso quando terminar.
                    </span>
                  </div>
                )}
                {uploading && (
                  <div className="space-y-1" data-testid="progress-bar">
                    <div className="h-2 rounded-full bg-gado-line overflow-hidden">
                      <div
                        className="h-full bg-gado-green transition-all"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gado-muted text-right">
                      {progress < 60 ? "Enviando vídeo…" : progress < 100 ? "IA analisando frames…" : "Finalizando…"} ({progress}%)
                    </p>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={startAnalysis}
                    disabled={uploading}
                    data-testid="analyze-button"
                    className="flex-1 bg-gado-gold hover:bg-gado-goldLight text-gado-ink font-semibold"
                  >
                    {uploading ? (
                      <>
                        <ArrowClockwise size={18} className="mr-2 animate-spin" /> {isLongVideo ? "Enviando…" : "Analisando…"}
                      </>
                    ) : isLongVideo ? (
                      <>
                        <Sparkle size={18} className="mr-2" weight="fill" /> Analisar em 2º plano
                      </>
                    ) : (
                      <>
                        <Sparkle size={18} className="mr-2" weight="fill" /> Analisar Lote
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    disabled={uploading}
                    onClick={() => {
                      setFile(null);
                      setVideoDuration(null);
                      if (fileRef.current) fileRef.current.value = "";
                    }}
                    data-testid="clear-video-button"
                  >
                    Trocar
                  </Button>
                </div>
              </div>
            )}
          </div>

          {current && current.status && current.status !== "completed" ? (
            <div className="rounded-xl border border-gado-gold/40 bg-gado-gold/5 p-5 text-center" data-testid="analysis-pending-card">
              <ArrowClockwise size={28} className={`mx-auto text-gado-gold mb-2 ${current.status === "processing" ? "animate-spin" : ""}`} />
              <p className="text-sm font-semibold text-gado-ink">
                {current.status === "processing" ? "Análise em segundo plano…" : "Esta análise falhou"}
              </p>
              <p className="text-xs text-gado-muted mt-1">
                {current.status === "processing"
                  ? "Você pode continuar usando o app — avisaremos quando terminar."
                  : (current.error || "Tente enviar o vídeo novamente.")}
              </p>
            </div>
          ) : current && (
            <AnalysisResult
              analysis={current}
              onCalibrate={onCalibrate}
              onDelete={onDelete}
            />
          )}
        </div>

        {/* History */}
        <div className="lg:col-span-5">
          <div className="rounded-xl border border-gado-line bg-white p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] uppercase tracking-widest text-gado-muted">Análises anteriores</p>
              <span className="text-xs text-gado-muted font-mono">{history.length}</span>
            </div>
            {loadingHistory ? (
              <p className="text-sm text-gado-muted text-center py-6">Carregando…</p>
            ) : history.length === 0 ? (
              <p className="text-sm text-gado-muted text-center py-6">Nenhuma análise ainda.</p>
            ) : (
              <ul className="space-y-2" data-testid="analyses-history">
                {history.map((a) => (
                  <li
                    key={a.analysis_id}
                    onClick={() => {
                      if (a.status === "processing") {
                        toast.info("Análise ainda em andamento — avisaremos quando terminar.");
                        return;
                      }
                      setCurrent(a);
                    }}
                    data-testid={`analysis-item-${a.analysis_id}`}
                    className="cursor-pointer rounded-lg border border-gado-line px-3 py-2 hover:bg-gado-bg/60 transition-colors"
                  >
                    {a.status === "processing" ? (
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gado-ink truncate">
                            {a.video_filename || "Vídeo"} — analisando…
                          </p>
                          <p className="text-xs text-gado-muted">{formatDate(a.created_at)} · em segundo plano</p>
                        </div>
                        <Badge
                          variant="outline"
                          className="border-gado-gold/40 bg-gado-gold/10 text-gado-gold text-[10px] px-2 py-0.5 inline-flex items-center gap-1"
                          data-testid={`processing-badge-${a.analysis_id}`}
                        >
                          <ArrowClockwise size={11} className="animate-spin" /> Processando
                        </Badge>
                      </div>
                    ) : a.status === "failed" ? (
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gado-ink truncate">
                            {a.video_filename || "Vídeo"} — falhou
                          </p>
                          <p className="text-xs text-gado-muted">{formatDate(a.created_at)}</p>
                        </div>
                        <Badge variant="outline" className="border-destructive/40 bg-destructive/10 text-destructive text-[10px] px-2 py-0.5">
                          Falhou
                        </Badge>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gado-ink truncate">
                            {a.result?.total_animals || 0} cabeças · {a.result?.breed_guess || "—"}
                          </p>
                          <p className="text-xs text-gado-muted">
                            {formatDate(a.created_at)} · {formatNum(a.result?.total_weight_kg, 0)} kg
                          </p>
                        </div>
                        <ConfidenceBadge value={a.result?.confidence} />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
